import React, { useEffect, useState, useRef, useCallback } from "react";
import { ChevronDown, X } from "lucide-react";

interface Partner {
  _id: string;
  companyName: string;
  isDeleted?: boolean;
}

interface PartnerSearchDropdownProps {
  value: string | null | undefined; // currently selected partnerId
  onChange: (id: string | null) => void;
  fetchPartners?: (params: {
    search: string;
    page: number;
    limit: number;
  }) => Promise<{ partners: Partner[]; pagination: { pages: number } }>;
  placeholder: string;
  disabled?: boolean;
  /** Pre-loaded partner for display when editing (so we can show the name) */
  initialPartner?: Partner | null;
  /** Show "General / No partner" option */
  showGeneralOption?: boolean;
  /** Label for the general option */
  generalOptionLabel?: string;
  /**
   * Show a cross that clears the current selection. On the form that means
   * "back to General"; on the list filter it means "all clients again".
   * Off wherever the selection is fixed (an existing service's client).
   */
  allowClear?: boolean;
  /** Accessible label for the clear cross. */
  clearLabel?: string;
}

export const PartnerSearchDropdown: React.FC<PartnerSearchDropdownProps> = ({
  value,
  onChange,
  fetchPartners,
  placeholder,
  disabled,
  initialPartner,
  showGeneralOption = true,
  generalOptionLabel = "General (no partner)",
  allowClear = true,
  clearLabel = "Clear selection",
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(
    initialPartner || null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update selected partner when initialPartner changes
  useEffect(() => {
    if (initialPartner) {
      setSelectedPartner(initialPartner);
    }
  }, [initialPartner]);

  // Display label for the currently selected partner
  const selectedLabel = value
    ? value === "GENERAL"
      ? ""
      : selectedPartner?.companyName || ""
    : "";

  // Fetch partners based on search
  const doFetch = useCallback(
    async (q: string) => {
      if (!fetchPartners || q.length < 2) {
        setPartners([]);
        return;
      }

      setLoading(true);
      try {
        const res = await fetchPartners({ search: q, page: 1, limit: 50 });
        setPartners(res.partners);
      } catch {
        setPartners([]);
      } finally {
        setLoading(false);
      }
    },
    [fetchPartners],
  );

  // Debounced search when user types
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (inputValue.length >= 2) {
      debounceRef.current = setTimeout(() => {
        doFetch(inputValue);
      }, 300);
    } else {
      setPartners([]);
      setLoading(false);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, doFetch]);

  // Handle input focus - open dropdown
  const handleFocus = () => {
    setOpen(true);
  };

  // Handle input blur - reset if nothing selected
  const handleBlur = () => {
    // Small delay to allow clicking on dropdown items
    setTimeout(() => {
      setOpen(false);
      // Reset input to selected value or empty
      setInputValue("");
      setPartners([]);
    }, 200);
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setOpen(true);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setInputValue("");
        setPartners([]);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectPartner = (p: Partner | null | "GENERAL") => {
    if (p === "GENERAL") {
      onChange("GENERAL");
      setSelectedPartner(null);
    } else if (p) {
      onChange(p._id);
      setSelectedPartner(p);
    } else {
      onChange(null);
      setSelectedPartner(null);
    }
    setInputValue("");
    setPartners([]);
    setOpen(false);
    inputRef.current?.blur();
  };

  // If no fetchPartners provided, fall back to a disabled input
  if (!fetchPartners) {
    return (
      <input
        disabled
        value={selectedLabel}
        placeholder={placeholder}
        className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed"
      />
    );
  }

  // Show selected partner name or placeholder
  const displayValue = inputValue || selectedLabel || "";
  const displayPlaceholder = selectedLabel ? selectedLabel : placeholder;

  /**
   * Only a real partner can be cleared. "GENERAL" and null are already the
   * empty state, so a cross there would do nothing visible.
   */
  const canClear = allowClear && !disabled && !!value && value !== "GENERAL";

  const clear = () => {
    selectPartner(null);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Direct input field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={displayPlaceholder}
          title={selectedLabel || placeholder}
          className={`w-full text-sm border rounded-md pl-3 ${
            canClear ? "pr-14" : "pr-9"
          } py-2 bg-white transition-all ${
            open
              ? "border-blue-500 ring-2 ring-blue-100"
              : "border-gray-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          } ${
            disabled
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "hover:border-gray-400 cursor-text"
          } ${
            selectedLabel && !inputValue
              ? "font-medium text-gray-900"
              : "text-gray-900"
          }`}
        />

        {/* Clear cross (only with a partner selected) + chevron */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {canClear && (
            <button
              // Inside a <form> — without this it would submit on click.
              type="button"
              // mousedown, not click: the input's blur handler fires first
              // otherwise and the button is gone before the click lands.
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }}
              aria-label={clearLabel}
              title={clearLabel}
              className="p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 pointer-events-none ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {/* Dropdown */}
      {open && (inputValue.length >= 2 || showGeneralOption) && (
        <div className="absolute z-50 mt-2 w-full bg-white border-2 border-gray-200 rounded-lg shadow-xl overflow-hidden">
          <ul className="overflow-y-auto max-h-60 py-1">
            {/* "General / No partner" option - always show when dropdown is open */}
            {showGeneralOption && (
              <li
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectPartner("GENERAL");
                }}
                className={`px-3 py-2 cursor-pointer select-none text-sm transition-colors ${
                  value === "GENERAL" || !value
                    ? "bg-blue-500 text-white"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
              >
                <span className="truncate block">{generalOptionLabel}</span>
              </li>
            )}

            {/* Show loading state */}
            {loading && inputValue.length >= 2 && (
              <li className="px-3 py-4 text-sm text-gray-400 text-center">
                Searching...
              </li>
            )}

            {/* Show results only when searched */}
            {!loading && inputValue.length >= 2 && partners.length === 0 && (
              <li className="px-3 py-8 text-sm text-gray-400 text-center">
                No results for "{inputValue}"
              </li>
            )}

            {!loading && inputValue.length >= 2 && partners.length > 0 && (
              <>
                {partners.map((p) => (
                  <li
                    key={p._id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectPartner(p);
                    }}
                    className={`px-3 py-2 cursor-pointer select-none text-sm transition-colors flex items-center gap-2 ${
                      value === p._id
                        ? "bg-blue-500 text-white"
                        : "hover:bg-gray-100 text-gray-900"
                    }`}
                    title={p.companyName}
                  >
                    <span className="truncate flex-1 min-w-0">
                      {p.companyName}
                    </span>
                    {p.isDeleted && (
                      <span
                        className={`text-xs shrink-0 font-medium px-1.5 py-0.5 rounded ${
                          value === p._id
                            ? "bg-white/20 text-white"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        deleted
                      </span>
                    )}
                  </li>
                ))}
              </>
            )}

            {/* Show hint when less than 2 characters */}
            {inputValue.length > 0 &&
              inputValue.length < 2 &&
              !showGeneralOption && (
                <li className="px-3 py-4 text-sm text-gray-400 text-center italic">
                  Type at least 2 characters to search
                </li>
              )}
          </ul>
        </div>
      )}
    </div>
  );
};
